import * as PIXI from "pixi.js";
import { Symbol } from "src/Symbol";
import { GameEngine } from "src/server/GameEngine";
import { IntroView } from "src/scenes/IntroView";
import { soundManager } from "src/shared/UI/SoundManager";
import { GameView } from "src/scenes/GameView";
import AppSingleton from "src/shared/util/AppSingleton";
import { WinText } from "src/WinText";
import { getGameRuleHtmlElement } from "./GameRules";
import { InfoPopup } from "./shared/UI/InfoPopup";
import { gameAssetManager } from "./GameAssetManager";
import { gameStateSoundManager } from "./GameStateSoundManager";

class GameApp {
    // Constants for aspect ratio and screen dimensions
    private readonly MAX_ASPECT_RATIO = 20 / 9;
    private readonly HEIGHT_LANDSCAPE = 1080;
    private readonly HEIGHT_PORTRAIT = 1920;

    // PIXI Application instance
    private _app: PIXI.Application;

    // Intro view and loading progress tracking
    private _introView: IntroView | null = null;

    constructor() {
        // Use the singleton instance of PIXI.Application
        const appSingleton = AppSingleton.getInstance();
        this._app = appSingleton.app;
    }

    /**
     * Launch the game application.
     */
    public async launch() {
        await this.initApp();
        gameAssetManager.createGameAssetBundles();
        await gameAssetManager.loadLoadingScreenAssets();
        await gameAssetManager.loadFonts();
        await soundManager.playSound("startGame");
        this.setupIntro();
        await gameAssetManager.loadGameAssets(
            (progress: number) => { this._introView?.updateProgress(progress) },
            () => { this._introView?.showTapText() });

        this.setupResizeHandler();

        document.addEventListener("buttonClicked", async (_: Event) => {
            await soundManager.playSound("buttonClick");
        });

        document.addEventListener("specialButtonClicked", async (_: Event) => {
            await soundManager.playSound("specialButtonClick");
        });
    }

    /**
     * Initialize the PIXI application.
     */
    private async initApp() {
        await this._app.init({
            background: "#000",
            resizeTo: window,
            resolution: window.devicePixelRatio,
            autoDensity: true,
        });

        // Append the canvas to the document body
        document.body.appendChild(this._app.canvas);
    }

    /**
     * Set up the intro screen and start loading game assets.
     */
    private setupIntro() {
        this._introView = new IntroView();
        this._introView.on("onScreenClick", this.onIntroComplete.bind(this));
        this._app.stage.addChild(this._introView);

        this.resize();
    }

    /**
     * Set up the resize event handler.
     */
    private setupResizeHandler() {
        window.addEventListener("resize", () => this.resize());
    }

    /**
     * Handle window resizing and adjust the stage accordingly.
     */
    private resize() {
        const width = window.innerWidth;
        let height = window.innerHeight;
        // Detect mobile device (include iPad on iPadOS which may report as Mac)
        const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
        const isIos = /iPad|iPhone|iPod/.test(ua) || ((navigator as any).platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
        const isAndroid = /Android/.test(ua);
        const isMobile = /Mobi|Opera Mini|IEMobile/.test(ua) || isIos || isAndroid;

        if (isMobile) {
            height *= 0.96; // Add 50px bottom padding for mobile
        }

        this._app.renderer.resolution = window.devicePixelRatio || 1;
        this._app.stage.x = width / 2;
        this._app.stage.y = height / 2;

        const ratio = width / height;
        const scale =
            height / (ratio < 1.4 ? this.HEIGHT_PORTRAIT : this.HEIGHT_LANDSCAPE);
        this._app.stage.scale.set(scale);

        this._app.renderer.resize(width, height);
        this._app.stage.emit("resize", { scale, ratio });
    }

    /**
     * Handle the completion of the intro screen.
     */
    private async onIntroComplete() {
        this._app.stage.removeChildren();
        await this.initializeGameComponents();

        const gameView = new GameView();
        this._app.stage.addChild(gameView);

        await gameStateSoundManager.updateBgMusic("gameStart");

        InfoPopup.setContent(getGameRuleHtmlElement());
        this.resize();
    }

    /**
     * Initialize game components like symbols, paylines, and particles.
     */
    private async initializeGameComponents() {
        await GameEngine.getInstance().init();
        Symbol.createPool(60);
        WinText.createPool(5);
    }
}

// Launch the game
const gameApp = new GameApp();
gameApp.launch();

export default gameApp;
